import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"
import { type SkillSpecialty, SkillSpecialtyValues } from "@/types/__generated__/union-types"

export const SpecialtyFilter = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const specialties = useShiftPlanFiltersStore((state) => state.filters.specialties)
  const setSpecialties = useShiftPlanFiltersStore((state) => state.actions.setSpecialties)
  const isLoading = useShiftPlanFiltersStore((state) => state.isLoading)
  const count = specialties.length

  const sortedOptions = [...SkillSpecialtyValues].sort((a, b) =>
    t(`scheduling.waitingRoom.filters.serviceSpecialtyFilter.${a}`).localeCompare(
      t(`scheduling.waitingRoom.filters.serviceSpecialtyFilter.${b}`),
      language,
    ),
  )

  const handleCheckChange = (item: SkillSpecialty) => {
    const index = specialties.indexOf(item)
    const newSpecialties = [...specialties]

    if (index < 0) newSpecialties.push(item)
    else newSpecialties.splice(index, 1)

    setSpecialties(newSpecialties)
  }

  const handleReset = () => {
    setSpecialties([])
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isLoading}
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer",
          isLoading && "cursor-not-allowed!",
        )}>
        <Chip
          data-testid="shift-plan-specialty-filter-trigger"
          className="h-8 gap-0.5"
          checked={count > 0}
          type="button">
          <div className={`overflow-hidden transition-all duration-200 ${count > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{t("shiftPlanOverview.filters.specialty") + (count > 0 ? ` (${count})` : ``)}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="shift-plan-specialty-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <ScrollArea className="max-h-80">
          <div className="grid gap-0.5">
            {sortedOptions.map((item) => (
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                className="h-9"
                key={item}
                checked={specialties.includes(item)}
                data-testid={`shift-plan-menu-specialty-${item.toLowerCase()}`}
                onCheckedChange={() => handleCheckChange(item)}>
                {t(`scheduling.waitingRoom.filters.serviceSpecialtyFilter.${item}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        </ScrollArea>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleReset}
            data-testid="shift-plan-reset-specialty-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
